/** @jsx jsx */
// @ts-ignore
import { jsx, Box } from 'theme-ui'
import { useState, FC, useEffect, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import usePaginatedCollection from '~/hooks/use-paginated-collection'
import { PaginationType, ProductItemComponentType, ProductItemType } from '~/@types/models'
import CollectionRichSnippet from '~/components/rich-snippets/collection-rich-snippet'
import Filter, { EFilterName } from '~/components/filter'
import { space } from '~/gatsby-plugin-theme-ui'
import { isEmpty } from 'lodash'

type Props = {
  children: (data: { products: readonly ProductItemType[] }) => React.ReactNode
  pagination: PaginationType
  initialData: readonly ProductItemType[]
  collectionHandle: string
  showFilter: boolean
  filters: any
}

const CollectionProducts = ({
  children,
  pagination,
  initialData,
  collectionHandle,
  filters,
}: Props) => {
  const infiniteScrollRef = useRef()
  const { total, perPage } = pagination
  const [selectedFilters, setselectedFilters] = useState({})
  const [inStockOnlyBool, setInStockOnlyBool] = useState<boolean>(true)
  const {
    data: collectionProducts,
    loadMore,
    hasMore,
    setCurrentFilters,
    resetFilters,
    isFetching,
  } = usePaginatedCollection({
    initialPage: 1,
    collectionHandle,
    total,
    perPage,
    initialData,
  })

  useEffect(() => {
    const localFilters = localStorage.getItem(EFilterName.filter)
    if (localFilters) {
      const parsedLocalFilters = JSON.parse(localFilters)
      if (parsedLocalFilters.type !== collectionHandle) {
        localStorage.removeItem(EFilterName.filter)
        return
      }
      setselectedFilters(parsedLocalFilters.filters)
      setCurrentFilters(state => ({
        ...state,
        inStockOnly: inStockOnlyBool,
        selectedFilters: parsedLocalFilters.filters,
        type: collectionHandle,
      }))
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(selectedFilters)) {
      const localFilters = localStorage.getItem(EFilterName.filter) || '{}'
      const collectionFilters = {
        ...JSON.parse(localFilters),
        type: collectionHandle,
        filters: selectedFilters,
      }
      localStorage.setItem(EFilterName.filter, JSON.stringify(collectionFilters))
      setCurrentFilters(state => ({
        ...state,
        selectedFilters,
      }))
    } else {
      resetFilters()
    }
  }, [selectedFilters])

  return (
    <Box
      sx={{
        marginTop: [space.md1, null],
        ...(collectionHandle === 'new-arrivals' && {
          '.ProductLabel--New': {
            display: 'none',
          },
        }),
      }}
    >
      {filters && filters.length > 0 && (
        <Filter
          infiniteScrollRef={infiniteScrollRef}
          categories={filters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setselectedFilters}
          inStockOnlyBool={inStockOnlyBool}
          setInStockOnlyBool={setInStockOnlyBool}
          resetFilters={resetFilters}
        />
      )}
      {isFetching ? (
        <h4>Loading...</h4>
      ) : (
        <InfiniteScroll
          ref={infiniteScrollRef}
          dataLength={collectionProducts.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
        >
          {children({
            products: collectionProducts,
          })}
        </InfiniteScroll>
      )}
      <CollectionRichSnippet data={initialData as ProductItemComponentType[]} />
    </Box>
  ) as JSX.Element
}

export default CollectionProducts
